import {useState, useMemo, useCallback} from "react";
import {Users, Info, AlertCircle} from "lucide-react";
import * as m from "@/paraglide/messages";
import {cn} from "@/lib/utils";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {Button} from "@/components/ui/button";
import {Skeleton} from "@/components/ui/skeleton";
import {SentimentToggle} from "./sentiment-toggle";
import {InfluencerCard} from "./influencer-card";
import {InfluencerPostsModal} from "./influencer-posts-modal";
import {
  useSentimentInfluencers,
  filterInfluencersBySentiment,
  countInfluencersBySentiment,
  type SentimentType,
} from "@/hooks/use-sentiment-influencers";
import type {SentimentInfluencerWithPosts, InfluencerMetricKey} from "@/lib/influencers-api";
import {INFLUENCER_METRICS} from "@/lib/influencers-api";

interface SentimentInfluencersSectionProps {
  locationId: string | null;
  className?: string;
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="bg-card/60 border-border/40 animate-pulse rounded-xl border p-5 backdrop-blur-sm"
        >
          <div className="mb-4 flex items-start gap-3">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
          <Skeleton className="mb-4 h-10 w-full" />
          <div className="space-y-2.5">
            {[1, 2, 3, 4, 5, 6].map((j) => (
              <div key={j} className="space-y-1">
                <div className="flex justify-between">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-3 w-12" />
                </div>
                <Skeleton className="h-3.5 w-full rounded-full" />
              </div>
            ))}
          </div>
          <Skeleton className="mt-5 h-9 w-full" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({sentiment}: {sentiment: SentimentType}) {
  return (
    <div className="bg-card/30 border-border/30 flex flex-col items-center justify-center rounded-xl border py-16 text-center backdrop-blur-sm">
      <Users className="text-muted-foreground/50 mb-4 h-12 w-12" />
      <p className="text-muted-foreground text-sm">
        {sentiment === "positive" ? m.influencers_empty_positive() : m.influencers_empty_negative()}
      </p>
    </div>
  );
}

function SelectLocationState() {
  return (
    <div className="bg-card/30 border-border/30 flex flex-col items-center justify-center rounded-xl border py-16 text-center backdrop-blur-sm">
      <Users className="text-muted-foreground/50 mb-4 h-12 w-12" />
      <p className="text-muted-foreground text-sm">{m.influencers_select_location()}</p>
    </div>
  );
}

function ErrorState({error}: {error: Error}) {
  return (
    <div className="bg-destructive/5 border-destructive/20 flex flex-col items-center justify-center rounded-xl border py-16 text-center">
      <AlertCircle className="text-destructive/70 mb-4 h-12 w-12" />
      <p className="text-destructive text-sm">{error.message}</p>
    </div>
  );
}

export function SentimentInfluencersSection({
  locationId,
  className,
}: SentimentInfluencersSectionProps) {
  const [selectedSentiment, setSelectedSentiment] = useState<SentimentType>("positive");
  const [selectedInfluencer, setSelectedInfluencer] = useState<SentimentInfluencerWithPosts | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {data: influencers, isLoading, error} = useSentimentInfluencers(locationId);

  // Calculate counts for toggle
  const counts = useMemo(() => {
    if (!influencers) return {positive: 0, negative: 0};
    return countInfluencersBySentiment(influencers);
  }, [influencers]);

  // Filter influencers by selected sentiment
  const filteredInfluencers = useMemo(() => {
    if (!influencers) return [];
    return filterInfluencersBySentiment(influencers, selectedSentiment);
  }, [influencers, selectedSentiment]);

  // Calculate max values for chart normalization
  const maxValues = useMemo(() => {
    if (!influencers || influencers.length === 0) return {};

    const result: Record<string, number> = {};
    for (const metric of INFLUENCER_METRICS) {
      result[metric.key] = Math.max(
        ...influencers.map((i) => (i[metric.key as InfluencerMetricKey] as number) || 0)
      );
    }
    return result;
  }, [influencers]);

  const handleViewPosts = useCallback((influencer: SentimentInfluencerWithPosts) => {
    setSelectedInfluencer(influencer);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    // Delay clearing the influencer to allow for exit animation
    setTimeout(() => setSelectedInfluencer(null), 200);
  }, []);

  return (
    <section className={cn("space-y-6", className)}>
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold tracking-tight">{m.influencers_title()}</h2>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground h-6 w-6"
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="max-w-xs text-sm">
                  <p className="mb-1 font-medium">{m.influencers_info_title()}</p>
                  <p className="text-muted-foreground text-xs">
                    {m.influencers_info_description()}
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            <p className="text-muted-foreground text-sm">{m.influencers_description()}</p>
          </div>
        </div>

        {/* Sentiment Toggle - only show when we have data */}
        {locationId && !isLoading && !error && influencers && influencers.length > 0 && (
          <SentimentToggle
            selected={selectedSentiment}
            counts={counts}
            onSelect={setSelectedSentiment}
            className="shrink-0"
          />
        )}
      </div>

      {/* Content */}
      {!locationId ? (
        <SelectLocationState />
      ) : isLoading ? (
        <LoadingSkeleton />
      ) : error ? (
        <ErrorState error={error} />
      ) : filteredInfluencers.length === 0 ? (
        <EmptyState sentiment={selectedSentiment} />
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {filteredInfluencers.map((influencer, index) => (
            <InfluencerCard
              key={influencer.id}
              influencer={influencer}
              maxValues={maxValues}
              onViewPosts={() => handleViewPosts(influencer)}
              style={{animationDelay: `${index * 50}ms`}}
              className="animate-in fade-in slide-in-from-bottom-2 duration-300"
            />
          ))}
        </div>
      )}

      {/* Posts Modal */}
      <InfluencerPostsModal
        influencer={selectedInfluencer}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </section>
  );
}
